import {
    AppBar,
    Box,
    CssBaseline,
    Drawer as MuiDrawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Toolbar,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
    Divider,
    Badge,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import {
    DashboardOutlined,
    UserAddOutlined
} from "@ant-design/icons";
import {
    EventNote,
    EmojiEvents,
    EventAvailable,
    MenuOpen,
    NotificationsNone,
    FullscreenExit,
    Fullscreen,
    ExpandLess,
    ExpandMore,
    FiberManualRecord,
    ConfirmationNumber,
    BookOutlined,
    CastForEducationOutlined,
    NotificationsOutlined,
    LocationCityOutlined,
    ForumOutlined,
    AccountCircle
} from "@mui/icons-material";
import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Dialogbox from "../Components/Dialogbox";
import { CustomIcons } from "src/Assets/Icons/Icons";
import text from "../Assets/Text/Text.json";
import man from "../Assets/Images/man.png";
import logo from "../Assets/Images/va_white_trans.png";
import "../Common/css/navbar.css";
import { getCommunityUnreadCount } from "src/Services/com_chat_api";
import { tokenCheck } from "src/Services/auth_api";

const drawerWidth = 200;
const collapsedWidth = 70;

interface Props {
    children: React.ReactElement;
}

const Drawer = styled(MuiDrawer)(({ theme }) => ({
    '& .MuiDrawer-paper': {
        width: drawerWidth,
        backgroundColor: "#1e1e1e",
        color: "#fff",
        borderRight: "1px solid #333",
    },
}));

export default function Navbar({ children }: Readonly<Props>) {
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const [openDrawer, setOpenDrawer] = React.useState(true); // for desktop toggle
    const [isOpen, setIsOpen] = React.useState(false);
    const [isFullscreen, setIsFullscreen] = React.useState(false);
    const [openSubmenus, setOpenSubmenus] = React.useState<Record<string, boolean>>({});
    const [communityUnreadCount, setCommunityUnreadCount] = React.useState<number>(0);
    const handleToggleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen();
            setIsFullscreen(true);
        } else {
            document.exitFullscreen();
            setIsFullscreen(false);
        }
    };
    const toggleSubmenu = (menuName: string) => {
        setOpenSubmenus((prev) => ({
            ...prev,
            [menuName]: !prev[menuName],
        }));
    };


    const theme = useTheme();
    const isMobile = useMediaQuery("(max-width:768px)");
    const token = sessionStorage.getItem("token");
    const user = sessionStorage.getItem("username") || "User";
    const navigate = useNavigate();
    const location = useLocation();

    React.useEffect(() => {
        if (!token) navigate("/");
    }, [token]);

    const path = location.pathname;
    const drawerItems = [
        {
            name: "Dashboard",
            icon: <DashboardOutlined style={{ fontSize: 20 }} />,
            onClick: () => navigate("/admin/admin-dashboard"),
            active: path === "/admin/admin-dashboard"
        }, {
            name: "Users",
            icon: <UserAddOutlined style={{ fontSize: 20 }} />,
            children: [
                {
                    name: "Students",
                    onClick: () => navigate("/admin/users/students"),
                    active: path === "/admin/users/students",
                },
                {
                    name: "Instructors",
                    onClick: () => navigate("/admin/users/instructors"),
                    active: path === "/admin/users/instructors",
                },
                {
                    name: "Admins",
                    onClick: () => navigate("/admin/users/admins"),
                    active: path === "/admin/users/admins",
                },
            ],
        },
        {
            name: "Lessons",
            icon: <CastForEducationOutlined fontSize="small" />,
            onClick: () => { navigate("/admin/courses") },
            active: path === "/admin/courses"
        },
        {
            name: "Tickets",
            icon: <ConfirmationNumber fontSize="small" />,
            onClick: () => { navigate("/admin/tickets") },
            active: path === "/admin/tickets"
        },
        {
            name: "Notices",
            icon: <EventNote fontSize="small" />,
            onClick: () => { navigate("/admin/notices") },
            active: path === "/admin/notices"
        },
        {
            name: "Notifications",
            icon: <NotificationsOutlined fontSize="small" />,
            onClick: () => { navigate("/admin/notifications") },
            active: path === "/admin/notifications"
        },
        {
            name: "Institution Updates",
            icon: <LocationCityOutlined fontSize="small" />,
            onClick: () => { navigate("/admin/institution-updates") },
            active: path === "/admin/institution-updates"
        },
        {
            name: "Community Chat",
            icon: (
                <Badge
                    badgeContent={communityUnreadCount}
                    color="error"
                    overlap="rectangular"
                    invisible={communityUnreadCount === 0}
                >
                    <ForumOutlined fontSize="small" />
                </Badge>
            ),
            onClick: () => { navigate("/admin/admin-chats") },
            active: path === "/admin/admin-chats"
        },
        {
            name: "Profile",
            icon: <AccountCircle style={{ fontSize: 20 }} />,
            onClick: () => { navigate("/admin/profile") },
            active: path === "/admin/profile"
        }
    ];

    const handleTokenCheck = async () => {
        try {
            await tokenCheck(token)
        } catch (error: any) {
            if (error.status === 401) {
                handleLogout();
            }
        }
    }

    useEffect(() => {
        handleTokenCheck();
    }, []);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleDesktopDrawerToggle = () => {
        setOpenDrawer(prev => !prev);
    };

    const handleLogout = () => {
        sessionStorage.clear();
        navigate("/admin-login");
    };

    const userId = sessionStorage.getItem("adminId") || "";

    const handleGetCommunityUnreadCount = async () => {
        try {
            const res = await getCommunityUnreadCount(token || "", userId, "3");
            setCommunityUnreadCount(res.data?.unreadCount || 0);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        handleGetCommunityUnreadCount();
    }, []);

    let timeout: any = null;

    useEffect(() => {
        const handleClick = () => {
            clearTimeout(timeout);
            timeout = setTimeout(() => {
                handleGetCommunityUnreadCount();
                handleTokenCheck();
            }, 1000);
        };

        const handleMessagesRead = () => {
            handleGetCommunityUnreadCount();
        };

        window.addEventListener("click", handleClick);
        window.addEventListener("communityMessagesRead", handleMessagesRead);

        return () => {
            window.removeEventListener("click", handleClick);
            window.removeEventListener("communityMessagesRead", handleMessagesRead);
        };
    }, []);

    const drawerContent = (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between", marginTop: isMobile ? "60px" : 0 }}>
            <List>
                {drawerItems.map((item, index) => {
                    const hasChildren = !!item.children?.length;
                    const isExpanded = openSubmenus[item.name];

                    return (
                        <React.Fragment key={index}>
                            <ListItem disablePadding sx={{ display: "block" }}>
                                <ListItemButton
                                    onClick={() => {
                                        if (hasChildren) {
                                            toggleSubmenu(item.name);
                                        } else {
                                            if (item.onClick) {
                                                item.onClick();
                                            }
                                        }
                                    }}
                                    sx={{
                                        justifyContent: openDrawer ? "initial" : "center",
                                        px: 2,
                                        py: 1,
                                        m: 0.5,
                                        borderRadius: 2,
                                        color: item.active ? "#FFEC00" : "#ccc",
                                        backgroundColor: item.active ? "#2a2a2a" : "transparent",
                                        "&:hover": { backgroundColor: "#333" },
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            color: "inherit",
                                            minWidth: 0,
                                            mr: openDrawer ? 1.5 : "auto",
                                            justifyContent: "center",
                                            fontSize: "1.2rem",
                                        }}
                                    >
                                        {item.icon}
                                    </ListItemIcon>

                                    {openDrawer && (
                                        <>
                                            <ListItemText
                                                primary={item.name}
                                                primaryTypographyProps={{ fontSize: "13px" }}
                                            />
                                            {hasChildren && (
                                                isExpanded ? (
                                                    <ExpandLess sx={{ fontSize: 16, color: "#ccc" }} />
                                                ) : (
                                                    <ExpandMore sx={{ fontSize: 16, color: "#ccc" }} />
                                                )
                                            )}
                                        </>
                                    )}
                                </ListItemButton>
                            </ListItem>

                            {/* Show subitems only if expanded */}
                            {hasChildren && isExpanded && (item.children ?? []).map((child, cIndex) => (
                                <Box
                                    key={cIndex}
                                    sx={{
                                        position: "relative",
                                        display: openDrawer ? "flex" : "none",
                                        alignItems: "center",
                                        ml: openDrawer ? 1.5 : 0,
                                        pl: openDrawer ? 3 : 0,
                                        '&::before': {
                                            content: '""',
                                            position: "absolute",
                                            top: "20%",
                                            left: openDrawer ? 15 : 0,
                                            width: 32,
                                            height: 50,
                                            borderLeft: "1px solid rgb(255, 255, 255)",
                                            borderBottom: "2px solid rgb(244, 243, 238)",
                                            borderBottomLeftRadius: "6px",
                                            transform: "translateY(-50%)",
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: "relative",
                                            pl: 2,
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                left: 0,
                                                top: "50%",
                                                width: "12px",
                                                height: "1px",
                                                // bgcolor: "#555",
                                                transform: "translateY(-50%)",
                                            },
                                        }}
                                    >
                                        <ListItem disablePadding>
                                            <ListItemButton
                                                onClick={child.onClick}
                                                sx={{
                                                    justifyContent: openDrawer ? "initial" : "center",
                                                    px: 2,
                                                    py: 0.5,
                                                    m: 0.5,
                                                    borderRadius: 2,
                                                    border: child.active ? "1px solid rgb(244, 243, 238)" : "1px solid transparent",
                                                    color: child.active ? "#FFEC00" : "#aaa",
                                                    // backgroundColor: child.active ? "#2a2a2a" : "transparent",
                                                    background: child.active ? "linear-gradient(180deg,rgb(7, 7, 7) 0%, rgb(0, 0, 0) 50%,rgb(58, 58, 58) 100%) !important" : "transparent",
                                                    "&:hover": { backgroundColor: "#333" },
                                                }}
                                            >
                                                {openDrawer && (
                                                    <ListItemText primary={child.name} primaryTypographyProps={{ fontSize: "12px" }} />
                                                )}
                                            </ListItemButton>
                                        </ListItem>
                                    </Box>
                                </Box>

                            ))}
                        </React.Fragment>
                    );
                })}
            </List>

            <ListItem disablePadding sx={{ display: "block", mb: 1 }}>
                <ListItemButton
                    onClick={() => setIsOpen(true)}
                    sx={{
                        justifyContent: openDrawer ? "initial" : "center",
                        px: 2,
                        py: 1,
                        m: 0.5,
                        borderRadius: 2,
                        color: "#ccc",
                        "&:hover": { backgroundColor: "#333" },
                    }}
                >
                    <ListItemIcon
                        sx={{
                            color: "inherit",
                            minWidth: 0,
                            mr: openDrawer ? 1.5 : "auto",
                            justifyContent: "center",
                            fontSize: "1.2rem",
                        }}
                    >
                        {CustomIcons.Logout}
                    </ListItemIcon>
                    {openDrawer && (
                        <ListItemText
                            primary={text.navigation.logout}
                            primaryTypographyProps={{ fontSize: "13px" }}
                        />
                    )}
                </ListItemButton>
            </ListItem>

        </Box>
    );

    return (
        <Box sx={{ display: "flex", height: "100vh" }}>
            <CssBaseline />

            {/* App Bar */}
            <AppBar
                position="fixed"
                sx={{
                    bgcolor: "#1e1e1e",
                    width: isMobile ? "100%" : `calc(100% - ${openDrawer ? drawerWidth : collapsedWidth}px)`,
                    ml: isMobile ? 0 : `${openDrawer ? drawerWidth : collapsedWidth}px`,
                    zIndex: theme.zIndex.drawer + 1,
                    borderBottom: "1px solid #9d9d9b61",
                }}
            >
                <Toolbar
                    sx={{
                        minHeight: "64px",
                        justifyContent: "space-between",
                        px: 2,
                        maxWidth: "100%",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, border: "1px solid #9d9d9b61", borderRadius: "4px", padding: "0px 8px", backgroundColor: "#252525" }}>
                        <IconButton color="inherit" onClick={isMobile ? handleDrawerToggle : handleDesktopDrawerToggle}>
                            <MenuOpen style={{ fontSize: isMobile ? "16px" : "20px" }} />
                        </IconButton>
                        <Typography
                            variant="h6"
                            noWrap
                            sx={{
                                display: "flex",
                                fontSize: isMobile ? 12 : 13,
                                fontWeight: 500,
                                color: "#fff",
                            }}
                        >
                            ADMIN PORTAL
                        </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? 0.5 : 1.5 }}>
                        <Divider orientation="vertical" flexItem sx={{ borderColor: "#9d9d9b61", mx: isMobile ? 0.5 : 1 }} />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, border: "1px solid #9d9d9b61", borderRadius: "30px", backgroundColor: "#252525" }}>
                            <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}>
                                <IconButton sx={{ color: "#ccc", width: isMobile ? 30 : 35, height: isMobile ? 30 : 35 }} onClick={handleToggleFullscreen}>
                                    {isFullscreen ? (
                                        <FullscreenExit fontSize="small" />
                                    ) : (
                                        <Fullscreen fontSize="small" />
                                    )}
                                </IconButton>
                            </Tooltip>
                        </Box>

                        {/* <Box sx={{ display: "flex", alignItems: "center", gap: 1, border: "1px solid #9d9d9b61", borderRadius: "30px", backgroundColor: "#252525" }}>
                            <Tooltip title="Notifications">
                                <IconButton sx={{ color: "#ccc", width: isMobile ? 30 : 35, height: isMobile ? 30 : 35 }}>
                                    <NotificationsNone fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box> */}

                        <Tooltip title={`Logged in as ${user}`}>
                            <Box
                                sx={{
                                    cursor: "pointer",
                                    bgcolor: "#aaa",
                                    borderRadius: "50%",
                                    width: isMobile ? 30 : 35,
                                    height: isMobile ? 30 : 35,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    "&:hover": { bgcolor: "#bbb" },
                                }}
                            >
                                <img src={man} alt="user" style={{ width: isMobile ? "20px" : "26px" }} />
                            </Box>
                        </Tooltip>
                    </Box>

                </Toolbar>
            </AppBar>


            {/* Drawer */}
            {isMobile ? (
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{ keepMounted: true }}
                >
                    <Toolbar /> {/* Offset space for AppBar */}
                    {drawerContent}
                </Drawer>

            ) : (
                <Drawer
                    variant="permanent"
                    open
                    sx={{
                        width: openDrawer ? drawerWidth : collapsedWidth,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': {
                            width: openDrawer ? drawerWidth : collapsedWidth,
                            backgroundColor: "#1e1e1e",
                            borderRight: "1px solid #9d9d9b61",
                            color: "#fff",
                            transition: theme.transitions.create('width', {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.enteringScreen,
                            }),
                            overflowX: 'hidden',
                        },
                    }}
                >
                    <Toolbar />
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: openDrawer ? "flex-start" : "center",
                            px: openDrawer ? 2 : 0,
                            py: 2,
                            height: 64,
                        }}
                    >
                        <img
                            src={logo}
                            alt="Logo"
                            style={{ width: 30, height: "auto" }}
                        />
                        {openDrawer && (
                            <Typography
                                variant="h6"
                                noWrap
                                sx={{
                                    ml: 1.5,
                                    fontSize: 16,
                                    fontWeight: 600,
                                    color: "#fff",
                                }}
                            >
                                VANGUARD
                            </Typography>
                        )}
                    </Box>
                    <Divider sx={{ borderColor: "#333", mx: 1 }} />

                    {drawerContent}
                </Drawer>
            )}

            {/* Main content */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    bgcolor: "#a5a5a5ff",
                    p: 2,
                    pt: isMobile ? 9 : 9,
                    overflow: "auto",
                }}
            >
                <Dialogbox
                    open={isOpen}
                    title={text.navigation.dialogBoxTitle}
                    content={text.navigation.dialogBoxMessage}
                    agreeButtonText={text.navigation.logout}
                    disagreeButtonText={text.navigation.cancel}
                    onAgree={handleLogout}
                    onDisagree={() => setIsOpen(false)}
                    onClose={() => setIsOpen(false)}
                />
                {children}
            </Box>
        </Box>
    );
}

